import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import moment from 'moment-mini';
import { useRouter } from 'expo-router';
import WorkUsageDetails from './WorkUsageDetails';
import Text from '@/components/Typography/Text';
import { loadCachedUsage } from '@/lib/usageCache';
import { accountApi } from '@/lib/api';
import { buildChatBreakdown, formatUsageNumber as number } from './usageInsights';

export default function UsageActivityBreakdown({ accountId, refreshVersion, type, rows, days, now, tx, button, border, selectedChat, onSelectChat, loading }) {
  const router = useRouter();
  const chats = useMemo(() => buildChatBreakdown(rows), [rows]);
  const [expandedTask, setExpandedTask] = useState(null);
  const [visible, setVisible] = useState(8);
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [work, setWork] = useState({ rows: [], loading: true, error: false, hasMore: false });

  useEffect(() => {
    if (type !== 'work') return;
    let cancelled = false;
    setWork(previous => ({ ...previous, loading: true, error: false }));
    loadCachedUsage(accountId, `work:${days}:${moment(now).format('YYYY-MM-DD')}:${page}`, () => accountApi.getCompletedWorkUsage(page, {
      startDate: moment(now).startOf('day').subtract(days - 1, 'days').toISOString(),
      endDate: moment(now).toISOString(),
    }), { force: retry > 0 }).then(result => {
      if (!cancelled) setWork(previous => ({
        rows: page === 1 ? result.rows : [...previous.rows, ...result.rows],
        loading: false, error: false, hasMore: !!result.pagination?.hasMore,
      }));
    }).catch(() => { if (!cancelled) setWork(previous => ({ ...previous, loading: false, error: true })); });
    return () => { cancelled = true; };
  }, [accountId, type, page, days, now, retry, refreshVersion]);

  const activities = type === 'work' ? work.rows : chats.slice(0, visible);
  return <View style={{ gap: 12, marginTop: 16 }}>
    <Text size="s" color="secondary">{tx(type === 'work' ? 'workReceiptExplanation' : 'chatSpendExplanation')}</Text>
    {activities.map(activity => <View key={activity.id} style={{ paddingVertical: 14, borderBottomWidth: 1, borderColor: border, gap: 8 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 16 }}>
        <Text size="m" weight="bold" style={{ flex: 1 }}>{activity.title || tx(type === 'work' ? 'untitledWork' : 'untitledChat')}</Text>
        <View style={{ alignItems: 'flex-end', gap: 3 }}><Text size="m" weight="bold">{number(activity.credits)}</Text><Text size="xs" color="secondary">{tx('creditsColumn')}</Text></View>
      </View>
      <Text size="xs" color="secondary">{type === 'work' ? tx('completedOn', { date: moment(activity.completedAt).format('MMM D, YYYY') }) : tx('chatModels', { count: number(activity.count), models: activity.models.join(', ') })}</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {type === 'work' && button(tx(expandedTask === activity.id ? 'hideTaskBreakdown' : 'viewTaskBreakdown'), () => setExpandedTask(expandedTask === activity.id ? null : activity.id), expandedTask === activity.id)}
        {type === 'chats' && button(selectedChat === activity.id ? tx('clearModel') : tx('viewCharges'), () => onSelectChat(selectedChat === activity.id ? null : activity.id), selectedChat === activity.id, { accessibilityLabel: tx('filterChat', { title: activity.title || tx('untitledChat') }) })}
        {activity.link && button(tx(type === 'work' ? 'openWork' : 'openChat'), () => router.push(activity.link))}
      </View>
      {type === 'work' && expandedTask === activity.id && <WorkUsageDetails accountId={accountId} taskId={activity.id} tx={tx} button={button} refreshVersion={refreshVersion} />}
    </View>)}
    {type === 'work' && work.loading && <ActivityIndicator accessibilityLabel={tx('loadingWorkCosts')} />}
    {type === 'work' && work.error && <View style={{ gap: 10 }}><Text size="s" color="secondary">{tx('workCostsFailed')}</Text>{button(tx('retry'), () => setRetry(value => value + 1))}</View>}
    {type === 'work' && !work.loading && !work.error && work.hasMore && button(tx('moreWorkTasks'), () => setPage(value => value + 1))}
    {type === 'chats' && chats.length > visible && button(tx('showMoreChats'), () => setVisible(value => value + 20))}
    {!activities.length && !(type === 'work' && (work.loading || work.error)) && <Text size="s" color="secondary" style={{ paddingVertical: 16 }}>{tx(type === 'work' ? 'noCompletedWork' : loading ? 'loadingChatCosts' : 'noChatCosts')}</Text>}
  </View>;
}
